import { onlineManager } from '@tanstack/react-query';
import { createWriteClient, indexedDbPendingWriteStore } from './pending-writes';

/**
 * TanStack Query client persisted to IndexedDB from day one (PRD §2) — a tech in a
 * basement still sees today's schedule from cache. `gcTime` must be >= the persister
 * maxAge or entries get GC'd before restore. `offlineFirst` serves cache while offline.
 *
 * Mutations (PWA rung-1 items 3–4, PRD §4.4): `networkMode: offlineFirst` pauses a mutation
 * fired offline instead of failing it, and it retries with backoff. Because every mutation
 * carries a client-minted idempotency key (Foundation Rule 3), a "Complete job" double-tapped
 * on flaky LTE — or replayed on reconnect — applies exactly once (server dedupes on
 * (clientId, mutationId)). We resume paused mutations the moment the network returns.
 *
 * MAP 225b: the client is built around its writes (`createWriteClient`). Every replayable
 * write is saved, owner-stamped, before it is first attempted, and a write kept for its owner
 * (parked, or a 401) is never retried. See pending-writes.ts.
 */
/** Every read's defaults in the app; exported so a test can run under the same ones. */
export const APP_QUERY_DEFAULTS = {
  gcTime: 1000 * 60 * 60 * 24, // 24h — must be >= persistOptions.maxAge (identity.ts)
  staleTime: 1000 * 30,
  networkMode: 'offlineFirst',
  retry: 1,
} as const;

export const { queryClient, writes: pendingWrites } = createWriteClient({
  store: indexedDbPendingWriteStore,
  defaultOptions: { queries: APP_QUERY_DEFAULTS },
});

/**
 * ONE idea of "offline" (MAP 225b): `navigator.onLine === false`, and only that (225a's
 * R1), now for TanStack too. Without this, `onlineManager` starts out `true` and changes only
 * on an `online`/`offline` EVENT, so an app opened in a basement believed it was online: the
 * gate knew better (it read `navigator.onLine`), while a write retried three times into the
 * failed-write log instead of pausing, and the banner, the photo drain and the completion
 * barrier each had their own answer. `setEventListener` runs this at once, so the manager is
 * right before the first render.
 *
 * Anything else counts as online: a slow server, a captive portal or a proxy that fails keeps
 * the skeleton and then the error screen, never shop data (225a).
 *
 * The cleanup is a function even with no window (a test process), so TanStack never runs this
 * again on its own and a test's `setOnline` stands.
 */
export function navigatorConnectivity(setOnline: (online: boolean) => void): () => void {
  const sync = () => setOnline(globalThis.navigator?.onLine !== false);
  sync();
  const target = globalThis.window as Window | undefined;
  if (!target?.addEventListener) return () => {};
  target.addEventListener('online', sync);
  target.addEventListener('offline', sync);
  return () => {
    target.removeEventListener('online', sync);
    target.removeEventListener('offline', sync);
  };
}
onlineManager.setEventListener(navigatorConnectivity);

// Replay writes queued while offline as soon as connectivity returns (no iOS Background Sync —
// this fires on the online/visibility signal TanStack's onlineManager tracks).
//
// ⚠️ Belt AND braces, not the mechanism: `QueryClient.mount()` — which `QueryClientProvider`
// calls — already subscribes to `onlineManager` and resumes the queue itself, so this line
// running or not changes nothing about what goes out. Anything that needs to CONSTRAIN what
// goes out therefore cannot live here; see `orderVisitCompletion` in offline-replay.ts, and
// the write guard link in pending-writes.ts.
onlineManager.subscribe((online) => {
  if (online) void queryClient.resumePausedMutations();
});

// The saved cache, its persister and whose it is: identity.ts (MAP 225a).
